import React, { useState, useEffect } from 'react';
import { useNavigate, useParams, Link } from 'react-router-dom';
import axios from 'axios';
import {
  Zap, Shield, TrendingUp, DollarSign, ArrowRight, Loader2, CheckCircle2
} from 'lucide-react';
import { HSLogo } from '@/components/HSLogo';
import { Button } from '@/components/ui/button';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const SharedResults = () => {
  const { shareId } = useParams();
  const navigate = useNavigate();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [notFound, setNotFound] = useState(false);

  useEffect(() => {
    const fetch = async () => {
      try {
        const res = await axios.get(`${API}/income/shared/${shareId}`);
        setData(res.data);
      } catch {
        setNotFound(true);
      } finally {
        setLoading(false);
      }
    };
    fetch();
  }, [shareId]);

  const getMilestoneColor = (color) => {
    if (color === 'emerald') return { bg: 'bg-emerald-500/10', text: 'text-emerald-400', border: 'border-emerald-500/20', accent: 'bg-emerald-500' };
    if (color === 'violet') return { bg: 'bg-violet-500/10', text: 'text-violet-400', border: 'border-violet-500/20', accent: 'bg-violet-500' };
    return { bg: 'bg-amber-500/10', text: 'text-amber-400', border: 'border-amber-500/20', accent: 'bg-amber-500' };
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-emerald-500" />
      </div>
    );
  }

  if (notFound) {
    return (
      <div className="min-h-screen bg-[#050505] flex items-center justify-center">
        <div className="text-center">
          <p className="text-xl font-bold mb-2">Result not found</p>
          <p className="text-muted-foreground mb-4">This shared link may have expired.</p>
          <Button onClick={() => navigate('/')} className="bg-emerald-500 text-black hover:bg-emerald-400 rounded-sm">Go Home</Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#050505]">
      <div className="noise-overlay" />

      {/* Nav */}
      <nav className="sticky top-0 z-50 bg-[#050505]/80 backdrop-blur-md border-b border-white/5">
        <div className="max-w-4xl mx-auto px-6 h-14 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-2">
            <div className="w-7 h-7 bg-emerald-500 flex items-center justify-center">
              <Zap className="w-4 h-4 text-black" />
            </div>
            <span className="font-black text-base tracking-tight">HumanScaled</span>
          </Link>
          <Button onClick={() => navigate('/signup')} className="bg-emerald-500 text-black hover:bg-emerald-400 font-bold rounded-sm h-8 text-sm px-4" data-testid="shared-signup-btn">
            Calculate Yours
          </Button>
        </div>
      </nav>

      <div className="max-w-4xl mx-auto px-6 py-10 space-y-8">
        {/* Header */}
        <div className="text-center space-y-3" data-testid="shared-header">
          <div className="inline-flex items-center gap-2 px-3 py-1 border border-emerald-500/30 bg-emerald-500/10 text-emerald-400 text-sm font-mono">
            <Shield className="w-3.5 h-3.5" /> INCOME INSURANCE RESULT
          </div>
          <h1 className="text-2xl md:text-3xl font-bold">
            {data.user_name} calculated their path to <span className="text-emerald-400">income freedom</span>
          </h1>
          <p className="text-muted-foreground">
            Earning ${Math.round(data.current_salary).toLocaleString()}/year
          </p>
        </div>

        {/* Key Insight */}
        <div className="bg-gradient-to-r from-emerald-500/10 to-violet-500/10 border border-emerald-500/20 p-6 text-center" data-testid="shared-insight">
          <p className="text-xl md:text-2xl font-bold">{data.key_insight}</p>
          <p className="text-muted-foreground text-sm mt-2">at ${Math.round(data.monthly_per_client).toLocaleString()}/mo per client</p>
        </div>

        {/* Milestones */}
        <div className="grid md:grid-cols-3 gap-4" data-testid="shared-milestones">
          {(data.milestones || []).map((m, i) => {
            const mc = getMilestoneColor(m.color);
            return (
              <div key={i} className={`bg-[#0a0a0a] border ${mc.border} p-5 relative overflow-hidden`}>
                <div className={`absolute top-0 left-0 h-1 ${mc.accent}`} style={{ width: `${Math.min(m.salary_percentage, 100)}%` }} />
                <p className={`text-xs ${mc.text} uppercase tracking-wider mb-2`}>{m.name}</p>
                <div className="flex items-baseline gap-1 mb-1">
                  <span className="font-mono text-3xl font-bold text-white">{m.clients_needed}</span>
                  <span className="text-sm text-muted-foreground">clients</span>
                </div>
                <p className="font-mono text-sm text-muted-foreground mb-3">
                  ${Math.round(m.monthly_income).toLocaleString()}/mo
                  <span className={`ml-2 ${mc.text}`}>{m.salary_percentage}%</span>
                  <span className="text-xs"> of salary</span>
                </p>
                <p className={`text-xs ${mc.text} italic mt-2 pt-2 border-t border-white/5`}>"{m.message}"</p>
              </div>
            );
          })}
        </div>

        {/* Dual Income */}
        <div className="bg-[#0a0a0a] border border-white/5 p-6" data-testid="shared-dual">
          <h3 className="text-sm font-bold uppercase tracking-wider text-muted-foreground mb-4">Keep Your Job + Add Clients</h3>
          <div className="space-y-3">
            {(data.dual_income_scenarios || []).map((s, i) => (
              <div key={i} className="flex items-center justify-between py-2 border-b border-white/5 last:border-0">
                <span className="text-sm">{s.label}</span>
                <div className="flex items-center gap-3">
                  <span className="text-emerald-400 font-mono font-bold text-sm">{s.income_increase}</span>
                  <span className="font-mono text-sm text-white">${(s.total_annual || 0).toLocaleString()}<span className="text-muted-foreground text-xs">/yr</span></span>
                </div>
              </div>
            ))}
          </div>
        </div>

        {/* CTA */}
        <div className="border border-emerald-500/20 bg-gradient-to-r from-emerald-500/5 to-transparent p-8 text-center space-y-4" data-testid="shared-cta">
          <p className="text-lg font-bold">Want to see your own path to income freedom?</p>
          <p className="text-sm text-muted-foreground">"Security does not come from one employer. Security comes from optionality."</p>
          <Button onClick={() => navigate('/signup')} className="bg-emerald-500 text-black hover:bg-emerald-400 h-12 px-8 font-bold rounded-sm text-base" data-testid="shared-cta-btn">
            Calculate Your Income Insurance <ArrowRight className="w-5 h-5 ml-2" />
          </Button>
        </div>

        {/* Footer */}
        <div className="text-center pb-8">
          <p className="text-xs text-muted-foreground italic">"AI does not replace your skill. It multiplies your capacity."</p>
          <p className="text-[10px] text-muted-foreground mt-1">Powered by HumanScaled</p>
        </div>
      </div>
    </div>
  );
};

export default SharedResults;
